/**
 * The Run column: what is happening to one submission, in words, and the button that fits it
 * (2026-10-06). One component for every list on the marking page.
 *
 * 🔴 IT REPLACES THE QUEUE COLUMN, WHICH SHOWED THE MACHINE WORD. `dispatched` stays on a queue
 * entry after its run has ended — [X] on 2026-09-25 five entries read `dispatched` over runs that
 * had stored a grade — so a stage is read from the live feed's attempts, and the queue entry is
 * read only for a park, whose sentence is the dispatcher's own and stays the authority.
 * 🔴 RUN IS DISABLED WHILE A RUN IS IN PROGRESS. The server already refuses a second run of a
 * submission that is running or held (`poller.in_progress`); the page stops offering a click it
 * knows will be refused. And it is disabled whenever the server holds a teacher's Run —
 * `teacher_run_goes`, with the server's own sentence beside it, never a copy of the rule.
 * ⚠️ THE WEEKLY PAUSE DOES NOT DISABLE IT: that stops unattended grading only.
 */
import type { ReactNode } from "react";
import type { SubmissionRow } from "../api";
import { clock, Elapsed, place, since, Spin, STAGE_WORDS, useLive } from "../live";
import { Why } from "../ui";

/** Why a control is disabled, said where it sits — never only in a tooltip. */
export const CAN_RUN = "Running a grade uses the subscription grading runs on, so it needs the Run grading permission on this cohort.";
export const UNLISTED_RUN = "Classroom no longer lists this submission, so there is nothing to run.";
const NOT_HANDED_IN = "Classroom does not list this as handed in, so there is nothing to run yet.";
const RUNNING_WHY = "A run is grading this submission. A second one would grade the same commit "
  + "again, and the server refuses it until this one ends.";

/**
 * A park in a few words; the dispatcher's own sentence is behind `Why`.
 * ⚠️ `queued` AND `dispatched` ARE ABSENT ON PURPOSE: waiting its turn is the feed's to say, with
 * a place in the queue, and `dispatched` says nothing at all about whether anything runs.
 */
export const PARK_WORDS: Record<string, string> = {
  auto_grade_off: "Not graded · unattended grading is off",
  at_capacity: "Waiting for a free grader",
  engine_refused: "Refused · the grading engine",
  unsafe_subpath: "Refused · the link's folder",
  repeatedly_failed: "Failed twice · stored nothing",
  session_limited: "Paused · the subscription's limit",
  ref_unresolvable: "Link problem",
  repo_unreachable: "GitHub did not answer · tried again next sweep",
  weekly_paused: "Paused · weekly usage",
  daily_runs_reached: "Today's runs are used",
  // ⚠️ RUN IS THE ONLY WAY BACK, and the button beside it says Run: a sweep never releases it.
  cancelled: "Cancelled · not run",
};

export function RunCell({ sid, graded, queue, runnable, unlisted = false, may, busy, held,
                          reprocessing = false, cancelling = false, onRun, onReprocess,
                          onCancel }: {
  sid: string;
  /** A mark is stored for it, so the button reads Run again. */
  graded: boolean;
  /** The listing's queue entry, when the page has one — for a park's sentence. */
  queue?: SubmissionRow["queue"] | null;
  /** Classroom lists it as handed in; `undefined` when the page has no listing to say. */
  runnable?: boolean;
  unlisted?: boolean;
  may: boolean;
  busy: boolean;
  /** The server's sentence when a teacher's Run is held, else null. */
  held: string | null;
  reprocessing?: boolean;
  /** This row's Cancel is on its way to the server. */
  cancelling?: boolean;
  onRun: () => void;
  onReprocess?: (attemptId: string) => void;
  /** Take this submission out of the queue (2026-10-07). Absent where a list offers no Cancel. */
  onCancel?: () => void;
}) {
  const live = useLive();
  const run = live.feed?.runs.find((r) => r.submission_id === sid);
  const waiting = run ? undefined : live.feed?.queued.find((q) => q.submission_id === sid);

  let state: ReactNode = null;
  let control: ReactNode;
  if (run && run.stage === "held") {
    // ⚠️ EVERY `{" "}` HERE IS LOAD-BEARING: JSX drops a space that ends a line, and the words
    // ran together — "Retrying the checkat 11:40", "Queuednext" — for a screen reader and for
    // anybody who copies the row (found by a test, 2026-10-06).
    state = (
      <span className="rc-state">
        <Spin dim /> Retrying the check
        {run.retry_finalise_at && <>{" "}<span className="rc-t">at {clock(run.retry_finalise_at)}</span></>}
      </span>);
    control = run.reprocessable && onReprocess
      ? <button className="btn sm" disabled={!may || reprocessing}
                title={!may ? CAN_RUN : undefined}
                onClick={() => onReprocess(run.attempt_id)}>
          {reprocessing ? "Reprocessing…" : "Reprocess"}</button>
      : <button className="btn sm" disabled>Running…</button>;
  } else if (run) {
    state = (
      <span className="rc-state">
        <Spin dim={run.stage === "starting" || run.stage === "waiting"} />
        {run.regrade ? (run.stage === "grading" ? "Re-grading" : STAGE_WORDS[run.stage])
                     : STAGE_WORDS[run.stage]}
        {run.stage !== "starting" && <>{" "}<Elapsed since={since(run)} /></>}
      </span>);
    control = (
      <span className="rc-ctl">
        <button className="btn sm" disabled>Running…</button>
        <Why label="Why Run is not offered">{RUNNING_WHY}</Why>
      </span>);
  } else if (waiting) {
    state = (
      <span className="rc-state">
        <span className="ring" aria-hidden="true" /> Queued{" "}
        <span className="rc-t rc-place">{place(waiting.position)}</span>
      </span>);
    // 🔴 CANCEL, NOT A DISABLED "QUEUED" (owner, 2026-10-07): after Run all a teacher must be able
    // to take back what has not started. It spends nothing, so it needs only the permission that
    // queued it; a run already grading is never offered one.
    control = onCancel
      ? <button className="btn sm" disabled={!may || cancelling || busy}
                title={!may ? CAN_RUN : undefined} onClick={onCancel}>
          {cancelling ? "Cancelling…" : "Cancel"}</button>
      : <button className="btn sm" disabled>Queued</button>;
  } else {
    const park = queue && PARK_WORDS[queue.reason];
    if (park) {
      state = (
        <span className="rc-state park">
          {park}
          {queue!.detail && <Why label="Why it is waiting">{String(queue!.detail)}</Why>}
        </span>);
    } else if (!graded) {
      state = <span className="rc-state muted">Not graded yet</span>;
    }
    const why = !may ? CAN_RUN
      : held ? held
      : unlisted ? UNLISTED_RUN
      : runnable === false ? NOT_HANDED_IN
      : "";
    control = (
      <span className="rc-ctl">
        <button className="btn sm" disabled={why !== "" || busy} onClick={onRun}>
          {graded || queue?.reason === "repeatedly_failed" ? "Run again" : "Run"}</button>
        {why && why !== CAN_RUN && <Why label="Why Run is not offered">{why}</Why>}
      </span>);
  }

  return (
    <div className="rc">
      {state}
      {control}
    </div>
  );
}
